import { Component, OnInit, PLATFORM_ID, Inject } from '@angular/core';
import {
  CommonModule,
  isPlatformBrowser,
  isPlatformServer,
} from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { Grave } from '../../core/models/grave.model';
import { Pagination } from '../../core/models/pagination.model';
import { GraveService } from '../../core/services/grave.service';
import { ZskService } from '../../core/services/zsk.service';
import { ZGender, ZZone, ZGraveType } from '../../core/models/zsk.model';
import { GenderComponent } from '../../shared/components/zsk/gender/gender.component';
import { ZoneComponent } from '../../shared/components/zsk/zone/zone.component';
import { GraveTypeComponent } from '../../shared/components/zsk/grave-type/grave-type.component';
import { DatePickerComponent } from '../../shared/components/date-picker/date-picker.component';
import { LabelComponent } from '../../shared/components/label/label.component';
import {
  ControlsOf,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';
import { GraveSearchForm } from '../../core/models/grave-search.model';
import { GraveType } from '../../core/enum/grave-type.enum';

@Component({
  selector: 'app-grave-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    RouterModule,
    GenderComponent,
    ZoneComponent,
    GraveTypeComponent,
    DatePickerComponent,
    LabelComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './grave-search.component.html',
  styleUrls: ['./grave-search.component.scss'],
})
export class GraveSearchComponent implements OnInit {
  graves: Grave[] = [];
  pagination: Pagination = {
    currentPage: 1,
    itemsPerPage: 6,
    totalItems: 0,
    totalPages: 0,
  };
  loading = false;
  noResults = false;
  currentLang: string;
  form!: FormGroup<ControlsOf<GraveSearchForm>>;

  // Advanced search properties
  showAdvancedSearch = false;

  constructor(
    private graveService: GraveService,
    public translateService: TranslateService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private fb: FormBuilder
  ) {
    this.currentLang =
      this.translateService.currentLang ||
      this.translateService.defaultLang ||
      'en';
    this.translateService.onLangChange.subscribe((event) => {
      this.currentLang = event.lang;
    });
  }

  ngOnInit(): void {
    // Only load initial graves if we're in the browser
    if (isPlatformBrowser(this.platformId)) {
      this.initForm();
      this.loadGraves();
    } else if (isPlatformServer(this.platformId)) {
      // On server, initialize with empty data but valid structure
      // This prevents "undefined" errors during SSR
      this.graves = [];
      this.initForm();
      this.pagination = {
        currentPage: 1,
        itemsPerPage: this.pagination.itemsPerPage,
        totalItems: 0,
        totalPages: 0,
      };
    }
  }

  initForm() {
    this.form = this.fb.group({
      searchTerm: new FormControl(''),
      gender: new FormControl(undefined),
      zone: new FormControl(undefined),
      graveType: new FormControl(GraveType.WellKnown),
      birthDate: new FormControl(undefined),
      deathDate: new FormControl(undefined),
    });
  }

  loadGraves() {
    this.loading = true;
    this.noResults = false;
    this.graveService
      .getGravePaginationWithFilter(
        this.pagination.currentPage,
        this.pagination.itemsPerPage,
        true,
        this.form.controls.searchTerm.value,
        // this.sortColumn,
        // this.sortDirection,
        this.form.controls.gender.value,
        this.form.controls.zone.value,
        this.form.controls.graveType.value,
        this.form.controls.birthDate.value,
        this.form.controls.deathDate.value
      )
      .subscribe({
        next: (response: any) => {
          if (response.result?.data && response.result.data.length > 0) {
            this.graves = response.result.data;

            // Ensure pagination data is properly handled for SSR
            if (response.pagination) {
              // Create a new pagination object to ensure state update
              this.pagination = {
                currentPage: response.pagination.currentPage || 1,
                itemsPerPage:
                  response.pagination.itemsPerPage ||
                  this.pagination.itemsPerPage,
                totalItems: response.pagination.totalItems || 0,
                totalPages: response.pagination.totalPages || 0,
              };
            }

            this.noResults = false;
          } else {
            this.graves = [];
            this.noResults = true;
          }
          this.loading = false;
        },
        error: (error: any) => {
          console.error('Error fetching graves:', error);
          this.loading = false;
          this.noResults = true;
        },
      });
  }

  search() {
    this.pagination.currentPage = 1; // Reset to first page on new search
    this.loadGraves();
  }

  toggleAdvancedSearch() {
    this.showAdvancedSearch = !this.showAdvancedSearch;
  }

  applyFilters() {
    this.pagination.currentPage = 1; // Reset to first page when applying filters
    this.loadGraves();
  }

  resetFilters() {
    const currentSearchTerm = this.form.controls.searchTerm.value;
    this.form.reset({
      searchTerm: undefined,
      gender: undefined,
      zone: undefined,
      graveType: GraveType.WellKnown,
      birthDate: undefined,
      deathDate: undefined,
    });
    this.pagination.currentPage = 1;
    this.loadGraves();
  }

  pageChanged(event: number) {
    this.pagination.currentPage = event;
    this.loadGraves();
    // Scroll to top when changing pages with smooth behavior
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  }

  isArabicLanguage(): boolean {
    return this.currentLang === 'ar';
  }

  /**
   * Returns an array of page numbers to display in pagination
   * Shows a window of pages around the current page
   */
  getPageRange(): number[] {
    // During SSR or if pagination isn't available, return empty array
    if (isPlatformServer(this.platformId) || !this.pagination) return [];

    const totalPages = this.pagination.totalPages || 0;
    const currentPage = this.pagination.currentPage || 1;

    // Return empty array if we have invalid pagination data
    if (totalPages <= 0) return [];

    const range: number[] = [];

    // Always show at least 5 pages if available
    const visiblePages = 5;

    // Calculate start and end of the page range
    let start = Math.max(1, currentPage - Math.floor(visiblePages / 2));
    let end = Math.min(totalPages, start + visiblePages - 1);

    // Adjust start if we're near the end
    if (end === totalPages) {
      start = Math.max(1, end - visiblePages + 1);
    }

    // Build the page range array
    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  /**
   * Returns a formatted string showing the current display range
   * Example: "Showing 1 to 10 of 100 results"
   */
  getDisplayedRange(): string {
    // Check for server-side rendering
    if (isPlatformServer(this.platformId)) {
      return '';
    }

    // Check for data availability
    if (!this.pagination || this.graves.length === 0 || this.loading) {
      return this.loading
        ? this.translateService.instant('SEARCH.LOADING_RESULTS')
        : this.translateService.instant('SEARCH.NO_ITEMS');
    }

    // Safely access pagination values
    const currentPage = this.pagination.currentPage || 1;
    const itemsPerPage = this.pagination.itemsPerPage || 6;
    const totalItems = this.pagination.totalItems || 0;

    // Calculate range
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(startItem + this.graves.length - 1, totalItems);

    return `${startItem} - ${endItem} ${this.translateService.instant(
      'SEARCH.OF'
    )} ${totalItems} ${this.translateService.instant('SEARCH.RESULTS')}`;
  }

  /**
   * Returns an array of page numbers to display in pagination
   * Shows a window of pages around the current page, with ellipsis for skipped pages
   */
  getPageNumbers(): (number | -1)[] {
    // During SSR or if pagination isn't available, return empty array
    if (isPlatformServer(this.platformId) || !this.pagination) return [];

    const totalPages = this.pagination.totalPages || 0;
    const currentPage = this.pagination.currentPage || 1;

    // Return empty array if we have invalid pagination data
    if (totalPages <= 0) return [];

    const pages: (number | -1)[] = [];

    // Always include first page, last page, current page, and pages immediately before and after current
    const pagesToShow = new Set<number>();
    pagesToShow.add(1); // First page
    pagesToShow.add(totalPages); // Last page
    pagesToShow.add(currentPage); // Current page

    // Pages around current
    if (currentPage > 1) pagesToShow.add(currentPage - 1);
    if (currentPage < totalPages) pagesToShow.add(currentPage + 1);

    // Add additional page on each side if space allows
    if (currentPage > 2) pagesToShow.add(currentPage - 2);
    if (currentPage < totalPages - 1) pagesToShow.add(currentPage + 2);

    // Convert to sorted array
    const sortedPages = Array.from(pagesToShow).sort((a, b) => a - b);

    // Add ellipsis where needed
    for (let i = 0; i < sortedPages.length; i++) {
      if (i > 0 && sortedPages[i] > sortedPages[i - 1] + 1) {
        pages.push(-1); // Add ellipsis
      }
      pages.push(sortedPages[i]);
    }

    return pages;
  }
}
